/** BHPH schedule + money math (Ohio RISA / ORC 1317.061 APR ≤ 25%). */

export const FEE_COUNT = 6;
export const DEFAULT_SERVICE_CONTRACT_FEE = 499;
/** Hard statutory cap — ORC 1317.061 alternative finance charge. */
export const OHIO_MAX_APR = 0.25;
/** Safe max under ORC 1317.061 (25% cap) — hair under to avoid rounding overage. */
export const DEFAULT_APR = 0.2499;

/** Parse APR from percent (18) or decimal (0.18). Clamped 0 … 25%. */
export function parseApr(v) {
  if (v == null || v === '') return 0;
  let n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 1) n = n / 100; // treat 18 as 18%
  return Math.min(OHIO_MAX_APR, Math.round(n * 100000) / 100000);
}

export function formatAprPercent(apr) {
  const a = parseApr(apr);
  return `${(a * 100).toFixed(a * 100 === Math.floor(a * 100) ? 0 : 2)}%`;
}

/** Biweekly amortizing payment for principal at APR over n periods. */
export function amortizingPayment(principal, nPay, apr) {
  const p = parseMoney(principal);
  const n = Math.max(0, Math.floor(Number(nPay) || 0));
  const rate = parseApr(apr);
  if (p <= 0 || n <= 0) return 0;
  if (rate <= 0) return Math.round((p / n) * 100) / 100;
  const r = rate / 26;
  const factor = Math.pow(1 + r, n);
  const pmt = (p * r * factor) / (factor - 1);
  return Math.round(pmt * 100) / 100;
}

/** Number of biweekly periods to amortize principal at APR with fixed payment. */
export function periodsForPayment(principal, paymentAmt, apr) {
  const p = parseMoney(principal);
  const pmt = parseMoney(paymentAmt);
  const rate = parseApr(apr);
  if (p <= 0 || pmt <= 0) return 0;
  if (rate <= 0) return Math.max(1, Math.ceil(p / pmt));
  const r = rate / 26;
  if (pmt <= p * r) {
    // Payment too small to cover interest — fall back to long term
    return Math.max(1, Math.ceil(p / Math.max(pmt - p * r, pmt * 0.01)));
  }
  const n = -Math.log(1 - (p * r) / pmt) / Math.log(1 + r);
  return Math.max(1, Math.ceil(n - 1e-9));
}

export function computeFinanceFromApr(principal, nPay, apr) {
  const p = Math.round(parseMoney(principal) * 100) / 100;
  const n = Math.max(0, Math.floor(Number(nPay) || 0));
  const rate = parseApr(apr);
  if (p <= 0 || n <= 0) {
    return { apr: rate, paymentAmt: 0, financeCharge: 0, timeBalance: 0, nPay: 0 };
  }
  const paymentAmt = amortizingPayment(p, n, rate);
  let timeBalance = Math.round(paymentAmt * n * 100) / 100;
  // Penny-fix last payment handled in schedule; FC is total of payments − principal
  let financeCharge = Math.round((timeBalance - p) * 100) / 100;
  if (financeCharge < 0) financeCharge = 0;
  timeBalance = Math.round((p + financeCharge) * 100) / 100;
  return { apr: rate, paymentAmt, financeCharge, timeBalance, nPay: n };
}

export function parseMoney(v) {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function money(n) {
  const x = Number(n) || 0;
  return x.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function parseDate(str) {
  if (!str) return null;
  const s = String(str).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function formatDate(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateInput(d) {
  if (!d || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDays(d, days) {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + days);
  return x;
}

export function feeRowsFromForm(form) {
  const rows = [];
  for (let i = 1; i <= FEE_COUNT; i++) {
    rows.push({
      label: (form[`fee${i}Label`] || '').trim(),
      amount: parseMoney(form[`fee${i}Amt`]),
    });
  }
  return rows;
}

function pushRow(rows, { date, amount, balanceAfter, kind, method, label }) {
  rows.push({
    num: rows.length + 1,
    date: formatDateInput(date),
    dateObj: new Date(date.getTime()),
    amount: Math.round(amount * 100) / 100,
    balanceAfter: Math.max(0, Math.round(balanceAfter * 100) / 100),
    kind: kind || 'installment',
    method: method || 'CARD DRAFT',
    label: label || null,
    interest: 0,
    principalPortion: 0,
    principalBalance: null,
  });
}

/**
 * Allocate each installment into interest vs principal at contracted APR
 * (biweekly declining-balance). Tax/title rows stay fee-only.
 */
export function annotateScheduleInterest(schedule, principal, apr) {
  const rate = parseApr(apr);
  const r = rate / 26;
  let bal = Math.round(parseMoney(principal) * 100) / 100;
  let interestTotal = 0;
  let principalTotal = 0;

  const out = (schedule || []).map((row) => {
    const next = { ...row };
    if (row.kind === 'tax_title') {
      next.interest = 0;
      next.principalPortion = 0;
      next.principalBalance = bal;
      return next;
    }

    const amt = parseMoney(row.amount);
    if (rate <= 0 || bal <= 0) {
      const prin = Math.min(amt, bal);
      bal = Math.max(0, Math.round((bal - prin) * 100) / 100);
      next.interest = 0;
      next.principalPortion = prin;
      next.principalBalance = bal;
      principalTotal += prin;
      return next;
    }

    let interest = Math.round(bal * r * 100) / 100;
    let principalPortion = Math.round((amt - interest) * 100) / 100;
    if (principalPortion > bal) {
      principalPortion = bal;
      interest = Math.round((amt - principalPortion) * 100) / 100;
    }
    if (principalPortion < 0) {
      interest = amt;
      principalPortion = 0;
    }
    bal = Math.max(0, Math.round((bal - principalPortion) * 100) / 100);
    next.interest = interest;
    next.principalPortion = principalPortion;
    next.principalBalance = bal;
    interestTotal += interest;
    principalTotal += principalPortion;
    return next;
  });

  return {
    schedule: out,
    interestTotal: Math.round(interestTotal * 100) / 100,
    principalTotal: Math.round(principalTotal * 100) / 100,
  };
}

/**
 * Customer stack:
 * 1) first installment at firstDue
 * 2) tax/title at taxTitleDue (skip installment that week)
 * 3) resume biweekly installments until principal paid
 */
export function buildBhphSchedule({
  unpaidBalance,
  paymentAmt,
  nPay,
  firstDue,
  taxTitleAmt,
  taxTitleDue,
}) {
  if (!firstDue || unpaidBalance <= 0) return [];
  const rows = [];
  let remaining = Math.round(unpaidBalance * 100) / 100;
  const tax = Math.round((taxTitleAmt || 0) * 100) / 100;
  const ttDue = taxTitleDue || addDays(firstDue, 14);

  // First installment
  let firstAmt = paymentAmt > 0 ? Math.min(paymentAmt, remaining) : remaining;
  if (nPay > 1 && paymentAmt <= 0) {
    firstAmt = Math.floor((remaining / nPay) * 100) / 100;
  }
  firstAmt = Math.round(firstAmt * 100) / 100;
  remaining = Math.round((remaining - firstAmt) * 100) / 100;
  pushRow(rows, {
    date: firstDue,
    amount: firstAmt,
    balanceAfter: remaining,
    kind: 'installment',
    label: 'Installment 1',
  });

  // Tax/title week (no installment)
  if (tax > 0) {
    pushRow(rows, {
      date: ttDue,
      amount: tax,
      balanceAfter: remaining,
      kind: 'tax_title',
      label: 'Tax/Title/License',
      method: 'CARD DRAFT',
    });
  }

  // Resume biweekly after tax/title week
  let due = addDays(ttDue, 14);
  let installmentNum = 2;

  if (paymentAmt > 0) {
    while (remaining > 0.009) {
      let amt = Math.min(paymentAmt, remaining);
      const after = Math.round((remaining - amt) * 100) / 100;
      if (after > 0 && after < 1) amt = remaining;
      amt = Math.round(amt * 100) / 100;
      remaining = Math.round((remaining - amt) * 100) / 100;
      pushRow(rows, {
        date: due,
        amount: amt,
        balanceAfter: remaining,
        kind: 'installment',
        label: `Installment ${installmentNum}`,
      });
      installmentNum += 1;
      if (remaining <= 0) break;
      due = addDays(due, 14);
      if (installmentNum > 500) break;
    }
  } else if (nPay > 1) {
    const leftCount = nPay - 1;
    for (let i = 0; i < leftCount && remaining > 0.009; i++) {
      let amt;
      if (i === leftCount - 1) amt = remaining;
      else {
        amt = Math.floor((remaining / (leftCount - i)) * 100) / 100;
        const after = Math.round((remaining - amt) * 100) / 100;
        if (after > 0 && after < 1) amt = remaining;
      }
      amt = Math.round(amt * 100) / 100;
      remaining = Math.round((remaining - amt) * 100) / 100;
      pushRow(rows, {
        date: due,
        amount: amt,
        balanceAfter: remaining,
        kind: 'installment',
        label: `Installment ${installmentNum}`,
      });
      installmentNum += 1;
      due = addDays(due, 14);
    }
  } else if (remaining > 0.009) {
    // Single remaining balloon if only one payment was intended but balance left
    pushRow(rows, {
      date: due,
      amount: remaining,
      balanceAfter: 0,
      kind: 'installment',
      label: `Installment ${installmentNum}`,
    });
    remaining = 0;
  }

  if (rows.length && remaining > 0.009) {
    const lastInst = [...rows].reverse().find((r) => r.kind === 'installment');
    if (lastInst) {
      lastInst.amount = Math.round((lastInst.amount + remaining) * 100) / 100;
      lastInst.balanceAfter = 0;
    }
  }

  return rows;
}

/** @deprecated use buildBhphSchedule */
export function buildSchedule(balance, regularAmt, count, firstDue) {
  return buildBhphSchedule({
    unpaidBalance: balance,
    paymentAmt: regularAmt,
    nPay: count,
    firstDue,
    taxTitleAmt: 0,
    taxTitleDue: firstDue ? addDays(firstDue, 14) : null,
  }).filter((r) => r.kind === 'installment');
}

export function compute(form) {
  const fees = feeRowsFromForm(form);
  const cash = parseMoney(form.cashPrice);
  const other = fees.reduce((s, r) => s + r.amount, 0);
  const serviceOn = !!form.serviceContract;
  const serviceFee = serviceOn
    ? parseMoney(form.serviceContractFee || DEFAULT_SERVICE_CONTRACT_FEE)
    : 0;
  const trade = parseMoney(form.tradeAmt);
  const down = parseMoney(form.downPayment);
  const taxTitle = parseMoney(form.taxTitleAmt);
  const apr = parseApr(form.apr ?? form.aprPercent ?? 0);
  const aprCapped = apr >= OHIO_MAX_APR - 1e-9;

  const cashPriceTotal = cash + other + serviceFee;
  const downAndTrade = down + trade;
  const unpaidBalance = Math.max(0, cashPriceTotal - downAndTrade);
  const principal = unpaidBalance;

  let paymentAmt = parseMoney(form.payAmount);
  let nPay = Math.max(0, Math.floor(parseMoney(form.numPayments)));
  let financeCharge = 0;
  let timeBalance = principal;
  let schedule = [];
  let lastPayment = paymentAmt;
  let warning = '';

  const sale = parseDate(form.saleDate);
  let firstDue = parseDate(form.firstDue);
  if (!firstDue && sale) firstDue = addDays(sale, 14);

  let taxTitleDate = form.taxTitleDate;
  if (firstDue && !taxTitleDate) {
    taxTitleDate = formatDateInput(addDays(firstDue, 14));
  } else if (sale && !taxTitleDate) {
    taxTitleDate = formatDateInput(addDays(sale, 28));
  }
  const taxTitleDue = parseDate(taxTitleDate);

  if (principal <= 0) {
    warning = 'No installment principal (fully covered by down/trade).';
    nPay = 0;
    paymentAmt = 0;
    financeCharge = 0;
    timeBalance = 0;
    if (taxTitle > 0 && taxTitleDue) {
      schedule = buildBhphSchedule({
        unpaidBalance: 0.01,
        paymentAmt: 0.01,
        nPay: 1,
        firstDue: firstDue || taxTitleDue,
        taxTitleAmt: taxTitle,
        taxTitleDue,
      }).filter((r) => r.kind === 'tax_title');
    }
  } else if (!firstDue) {
    warning = 'Enter sale date or first due date.';
  } else if (paymentAmt > 0 || nPay > 0 || apr > 0) {
    if (apr > 0) {
      // ORC 1317.061 path: true APR (biweekly amortizing) → precomputed finance charge $
      if (nPay <= 0 && paymentAmt > 0) {
        nPay = periodsForPayment(principal, paymentAmt, apr);
      }
      if (nPay <= 0) {
        warning = 'Enter # of biweekly payments (or a payment amount) with APR.';
      } else {
        const fin = computeFinanceFromApr(principal, nPay, apr);
        paymentAmt = fin.paymentAmt;
        financeCharge = fin.financeCharge;
        timeBalance = fin.timeBalance;
        nPay = fin.nPay;
        if (aprCapped) {
          warning = 'APR at Ohio statutory maximum (25% under ORC 1317.061).';
        }
      }
    } else {
      // Zero-APR legacy stack (still lawful under RISA with $0 finance charge)
      financeCharge = 0;
      timeBalance = principal;
      if (paymentAmt <= 0 && nPay > 0) {
        paymentAmt = Math.floor((timeBalance / nPay) * 100) / 100;
      }
      if (nPay <= 0 && paymentAmt > 0) {
        nPay = Math.ceil(timeBalance / paymentAmt);
      }
    }

    if (timeBalance > 0 && (paymentAmt > 0 || nPay > 0) && firstDue) {
      schedule = buildBhphSchedule({
        unpaidBalance: timeBalance,
        paymentAmt,
        nPay,
        firstDue,
        taxTitleAmt: taxTitle,
        taxTitleDue: taxTitleDue || addDays(firstDue, 14),
      });
      const installments = schedule.filter((r) => r.kind === 'installment');
      if (installments.length) {
        lastPayment = installments[installments.length - 1].amount;
        nPay = installments.length;
        // Reconcile FC to actual scheduled installment total (penny adjust)
        const scheduledTotal = installments.reduce((s, r) => s + parseMoney(r.amount), 0);
        timeBalance = Math.round(scheduledTotal * 100) / 100;
        financeCharge = Math.max(0, Math.round((timeBalance - principal) * 100) / 100);
      }
    }
  } else {
    warning = 'Enter payment amount and/or number of biweekly payments (and APR if charging interest).';
  }

  const annotated = annotateScheduleInterest(schedule, principal, apr);
  schedule = annotated.schedule;

  const installments = schedule.filter((r) => r.kind === 'installment');
  const firstDueDate = installments[0]?.dateObj || firstDue;
  const payoffDate = installments.length
    ? installments[installments.length - 1].dateObj
    : null;
  const remainingAfterFirst = Math.max(
    0,
    Math.round((timeBalance - (installments[0]?.amount || 0)) * 100) / 100
  );

  return {
    cash,
    other,
    serviceFee,
    serviceOn,
    cashPriceTotal,
    trade,
    down,
    downAndTrade,
    unpaidBalance,
    financeCharge,
    principal,
    timeBalance,
    apr,
    aprPercent: Math.round(apr * 10000) / 100,
    aprDisplay: formatAprPercent(apr),
    aprCapped,
    chargesInterest: apr > 0 && financeCharge > 0,
    taxTitle,
    taxTitleDate: taxTitleDate || '',
    paymentAmt: installments[0]?.amount || paymentAmt,
    nPay: installments.length || nPay,
    lastPayment,
    schedule,
    interestTotal: annotated.interestTotal,
    principalPaidTotal: annotated.principalTotal,
    warning,
    fees,
    firstDueDate,
    payoffDate,
    remainingAfterFirst,
  };
}

/** Days past due - installment rows only (tax/title tracked separately). */
export function computeDelinquency(schedule, payments, asOf = new Date()) {
  const today = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
  const lateStartDays = 5;

  const installmentsPaid = (payments || []).filter(
    (p) => !p.voided_at && (p.kind === 'installment' || p.kind === 'principal')
  );
  let paid = installmentsPaid.reduce((s, p) => s + parseMoney(p.amount), 0);
  paid = Math.round(paid * 100) / 100;

  let remainingToAllocate = paid;
  let earliestUnpaidDue = null;
  let remainingPrincipal = 0;

  const installmentRows = (schedule || []).filter(
    (r) => !r.kind || r.kind === 'installment'
  );

  for (const row of installmentRows) {
    const amt = parseMoney(row.amount);
    if (remainingToAllocate >= amt - 0.005) {
      remainingToAllocate = Math.round((remainingToAllocate - amt) * 100) / 100;
    } else {
      const stillDue = Math.round((amt - remainingToAllocate) * 100) / 100;
      remainingPrincipal += stillDue;
      remainingToAllocate = 0;
      const due = parseDate(row.date);
      if (due && due < today && !earliestUnpaidDue) {
        earliestUnpaidDue = due;
      }
    }
  }

  if (remainingToAllocate > 0) {
    remainingPrincipal = Math.max(
      0,
      Math.round((remainingPrincipal - remainingToAllocate) * 100) / 100
    );
  }

  let daysPastDue = 0;
  if (earliestUnpaidDue) {
    daysPastDue = Math.floor((today - earliestUnpaidDue) / (1000 * 60 * 60 * 24));
  }

  let bucket = 'current';
  if (remainingPrincipal <= 0.009) bucket = 'paid';
  else if (daysPastDue >= lateStartDays) bucket = '10plus';
  else if (daysPastDue >= 1) bucket = '1to9';

  const collected = (payments || [])
    .filter((p) => !p.voided_at)
    .reduce((s, p) => s + parseMoney(p.amount), 0);

  return {
    daysPastDue,
    bucket,
    remainingPrincipal,
    earliestUnpaidDue: earliestUnpaidDue ? formatDateInput(earliestUnpaidDue) : null,
    collected: Math.round(collected * 100) / 100,
  };
}

export function realizedProfit(costInCar, collected) {
  return Math.round((parseMoney(collected) - parseMoney(costInCar)) * 100) / 100;
}

/**
 * Cash-positive when BHPH cash collected >= cost in car.
 * Time-to-cash-positive estimates remaining on-time biweekly installments needed to close the gap.
 */
export function cashPositiveForAccount(account, asOf = new Date()) {
  const cost = parseMoney(account?.cost_in_car);
  const collected = parseMoney(account?.delinquency?.collected ?? account?.collected);
  const profit = Math.round((collected - cost) * 100) / 100;
  const gap = Math.max(0, Math.round((cost - collected) * 100) / 100);
  const isCashPositive = cost > 0 ? collected + 0.009 >= cost : collected > 0;
  const payAmt = parseMoney(account?.pay_amount);
  let periodsNeeded = 0;
  let weeksNeeded = 0;
  let estimatedDate = null;
  if (!isCashPositive && payAmt > 0.009) {
    periodsNeeded = Math.ceil(gap / payAmt);
    weeksNeeded = periodsNeeded * 2;
    const base =
      parseDate(account?.delinquency?.earliestUnpaidDue) ||
      parseDate(account?.first_due) ||
      asOf;
    estimatedDate = formatDateInput(addDays(base, (periodsNeeded - 1) * 14));
  } else if (isCashPositive) {
    periodsNeeded = 0;
    weeksNeeded = 0;
  }
  return {
    id: account?.id,
    buyer: account?.buyer_name || '-',
    vehicle: [account?.veh_year, account?.veh_make, account?.veh_model].filter(Boolean).join(' '),
    status: account?.status || 'active',
    cost,
    collected,
    profit,
    gap,
    isCashPositive,
    periodsNeeded,
    weeksNeeded,
    estimatedDate,
    payAmount: payAmt,
    remainingPrincipal: parseMoney(
      account?.delinquency?.remainingPrincipal ?? account?.unpaid_balance
    ),
  };
}

export function portfolioCashPositiveReport(accounts, asOf = new Date()) {
  const rows = (accounts || []).map((a) => cashPositiveForAccount(a, asOf));
  const cashPositive = rows.filter((r) => r.isCashPositive);
  const notYet = rows.filter((r) => !r.isCashPositive && (r.status === 'active' || !r.status));
  const totalCost = rows.reduce((s, r) => s + r.cost, 0);
  const totalCollected = rows.reduce((s, r) => s + r.collected, 0);
  const totalGap = notYet.reduce((s, r) => s + r.gap, 0);
  const withEstimate = notYet.filter((r) => r.periodsNeeded > 0);
  const avgWeeks =
    withEstimate.length > 0
      ? Math.round(
          (withEstimate.reduce((s, r) => s + r.weeksNeeded, 0) / withEstimate.length) * 10
        ) / 10
      : null;
  return {
    rows,
    cashPositive,
    notYet,
    counts: {
      total: rows.length,
      cashPositive: cashPositive.length,
      notYet: notYet.length,
      repossessed: rows.filter((r) => r.status === 'repossessed').length,
      paidOff: rows.filter((r) => r.status === 'paid_off' || r.remainingPrincipal <= 0.009)
        .length,
    },
    totals: {
      cost: Math.round(totalCost * 100) / 100,
      collected: Math.round(totalCollected * 100) / 100,
      realizedProfit: Math.round((totalCollected - totalCost) * 100) / 100,
      gapToPortfolioCashPositive: Math.round(totalGap * 100) / 100,
      avgWeeksToCashPositive: avgWeeks,
    },
  };
}

/** ORC 1317.12 statutory cure: cash at door vs expense remainder to time balance. */
export const ORC_1317_12_EXPENSE_DOOR_CAP = 25;

export function estimatePastDueInstallmentCount(account, asOf = new Date()) {
  if (account?.pastDueCount != null && account.pastDueCount !== '') {
    return Math.max(0, Math.floor(Number(account.pastDueCount) || 0));
  }
  const schedule = Array.isArray(account?.schedule_json) ? account.schedule_json : [];
  const today = asOf instanceof Date ? asOf : new Date(asOf);
  let dueCount = 0;
  for (const row of schedule) {
    if (row.kind === 'tax_title') continue;
    const d = parseDate(row.date || row.dateObj);
    if (d && d <= today) dueCount += 1;
  }
  if (dueCount > 0) {
    // Rough: unpaid ≈ due installments not covered by collected installment payments
    const payAmt = parseMoney(account?.pay_amount);
    const collected = parseMoney(account?.delinquency?.collected);
    const paidInstallApprox = payAmt > 0 ? Math.floor(collected / payAmt) : 0;
    return Math.max(0, dueCount - paidInstallApprox);
  }
  const dpd = Number(account?.delinquency?.daysPastDue) || 0;
  if (dpd <= 0) return 0;
  return Math.max(1, Math.ceil(dpd / 14));
}

/**
 * Itemized ORC 1317.12 cure package (A)-(D).
 * Expense over $25 need not be delivered at cure; added to time balance.
 */
export function computeOr131712Cure({
  account,
  pastDueCount: pastDueCountIn,
  lateFeeCharges = 0,
  declineFeeCharges = 0,
  otherDelinquencyCharges = 0,
  repoExpense = 640,
  storagePerDay = 20,
  storageDays = 0,
  asOf = new Date(),
} = {}) {
  const payAmt = parseMoney(account?.pay_amount);
  const pastDueCount =
    pastDueCountIn != null && pastDueCountIn !== ''
      ? Math.max(0, Math.floor(Number(pastDueCountIn) || 0))
      : estimatePastDueInstallmentCount(account, asOf);
  const pastDueAmount = Math.round(pastDueCount * payAmt * 100) / 100;
  const delinquencyCharges =
    Math.round(
      (parseMoney(lateFeeCharges) +
        parseMoney(declineFeeCharges) +
        parseMoney(otherDelinquencyCharges)) *
        100
    ) / 100;
  const storageCost = Math.round(parseMoney(storagePerDay) * parseMoney(storageDays) * 100) / 100;
  const totalExpenses = Math.round((parseMoney(repoExpense) + storageCost) * 100) / 100;
  const expenseAtDoor = Math.min(ORC_1317_12_EXPENSE_DOOR_CAP, totalExpenses);
  const expenseToBalance = Math.round((totalExpenses - expenseAtDoor) * 100) / 100;
  const twoPayDeposit = Math.round(payAmt * 2 * 100) / 100;
  const cashToCure =
    Math.round((pastDueAmount + delinquencyCharges + expenseAtDoor + twoPayDeposit) * 100) / 100;

  return {
    payAmt,
    pastDueCount,
    pastDueAmount,
    delinquencyCharges,
    lateFeeCharges: parseMoney(lateFeeCharges),
    declineFeeCharges: parseMoney(declineFeeCharges),
    otherDelinquencyCharges: parseMoney(otherDelinquencyCharges),
    repoExpense: parseMoney(repoExpense),
    storagePerDay: parseMoney(storagePerDay),
    storageDays: parseMoney(storageDays),
    storageCost,
    totalExpenses,
    expenseAtDoor,
    expenseToBalance,
    twoPayDeposit,
    cashToCure,
    doorCap: ORC_1317_12_EXPENSE_DOOR_CAP,
  };
}

/** Post-sale accounting (BHPH silo only - not LotSense bank ledger). */
export function computeSaleAccounting({
  amountOwed = 0,
  salePrice = 0,
  repoExpense = 0,
  storageCost = 0,
  otherCosts = 0,
  credits = 0,
  dealerBid = false,
} = {}) {
  const owed = parseMoney(amountOwed);
  const proceeds = parseMoney(salePrice);
  const costs =
    Math.round(
      (parseMoney(repoExpense) + parseMoney(storageCost) + parseMoney(otherCosts)) * 100
    ) / 100;
  const credit = parseMoney(credits);
  const net = Math.round((owed + costs - proceeds - credit) * 100) / 100;
  return {
    owed,
    proceeds,
    costs,
    credit,
    deficiency: net > 0 ? net : 0,
    surplus: net < 0 ? Math.abs(net) : 0,
    dealerBid: !!dealerBid,
  };
}
